import { useCallback, useEffect, useRef, useState } from 'react';
import './DuckGarden.css';

const SLOW_LOAD_MS = 20000;
const EXIT_MS = 380;

export default function DuckGarden({ onBack, onReady, onExited, exiting = false }) {
  const frameRef = useRef(null);
  const mountedRef = useRef(false);
  const exitingRef = useRef(exiting);
  const callbacksRef = useRef({ onReady, onExited });
  const readyReportedRef = useRef(false);
  const stateRef = useRef('loading');
  const [loadState, setLoadState] = useState('loading');
  const [frameRevision, setFrameRevision] = useState(0);

  exitingRef.current = exiting;
  callbacksRef.current = { onReady, onExited };

  const updateLoadState = useCallback((state) => {
    stateRef.current = state;
    setLoadState(state);
  }, []);

  const receiveState = useCallback((state) => {
    if (!mountedRef.current || exitingRef.current) return;
    if (state !== 'ready' && state !== 'error') return;
    if (stateRef.current === state) return;
    updateLoadState(state);

    if (state === 'ready') {
      // The child reports ready only after its fonts and first rendered frame.
      // Respect any parent control the visitor chose while it was loading.
      const activeElement = document.activeElement;
      const mayFocusGarden = !activeElement || activeElement === document.body ||
        activeElement === document.documentElement || activeElement === frameRef.current;
      if (mayFocusGarden) {
        try {
          const canvas = frameRef.current?.contentDocument?.getElementById('dw-canvas');
          canvas?.focus({ preventScroll: true });
        } catch {
          // An unexpected cross-origin navigation must not break Back/retry.
        }
      }
      if (!readyReportedRef.current) {
        readyReportedRef.current = true;
        callbacksRef.current.onReady?.();
      }
    }
  }, [updateLoadState]);

  const readFrameState = useCallback(() => {
    try {
      const state = frameRef.current?.contentDocument?.documentElement?.dataset.gardenState;
      receiveState(state);
    } catch {
      // The load timeout still offers retry if a redirect blocks DOM access.
    }
  }, [receiveState]);

  useEffect(() => {
    mountedRef.current = true;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onMessage = (event) => {
      const frameWindow = frameRef.current?.contentWindow;
      if (!frameWindow || event.origin !== window.location.origin || event.source !== frameWindow) return;
      if (event.data?.type === 'duck-garden:ready') receiveState('ready');
      if (event.data?.type === 'duck-garden:error') receiveState('error');
    };

    window.addEventListener('message', onMessage);
    // Also catch a cached child that finished before the listener was attached.
    readFrameState();
    return () => {
      mountedRef.current = false;
      window.removeEventListener('message', onMessage);
      document.body.style.overflow = previousOverflow;
    };
  }, [readFrameState, receiveState]);

  useEffect(() => {
    if (exiting || loadState !== 'loading') return;
    const timeout = window.setTimeout(() => {
      if (mountedRef.current && !exitingRef.current && stateRef.current === 'loading') {
        updateLoadState('slow');
      }
    }, SLOW_LOAD_MS);
    return () => window.clearTimeout(timeout);
  }, [exiting, frameRevision, loadState, updateLoadState]);

  useEffect(() => {
    if (!exiting) return;
    const finishExit = () => {
      if (mountedRef.current && exitingRef.current) callbacksRef.current.onExited?.();
    };
    const reduceMotion = typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      finishExit();
      return;
    }
    const timeout = window.setTimeout(finishExit, EXIT_MS);
    return () => window.clearTimeout(timeout);
  }, [exiting]);

  function retry() {
    if (exitingRef.current) return;
    readyReportedRef.current = false;
    updateLoadState('loading');
    // A fresh browsing context also makes messages from the old attempt stale.
    setFrameRevision(revision => revision + 1);
  }

  const showStatus = !exiting && (loadState === 'error' || loadState === 'slow');
  const modifier = exiting ? 'exiting' : loadState === 'ready' ? 'ready' :
    showStatus ? 'error' : 'entering';

  return (
    <section className={`duck-garden duck-garden--${modifier}`} aria-label="the quiet duck garden">
      <iframe
        key={frameRevision}
        ref={frameRef}
        className="duck-garden__frame"
        title="a little duck day"
        src="/garden/index.html"
        onLoad={readFrameState}
        onError={() => receiveState('error')}
      />
      {showStatus && (
        <div className="duck-garden__status" role="status">
          <p className="duck-garden__status-text">
            {loadState === 'slow'
              ? 'the garden is taking longer than usual to open — you can keep waiting or try again'
              : 'the garden couldn’t open — please try again'}
          </p>
          <button className="duck-garden__retry" type="button" onClick={retry}>try again</button>
        </div>
      )}
      <button className="duck-garden__back" type="button" onClick={onBack} aria-label="back to home">
        <span aria-hidden="true">←</span> back
      </button>
    </section>
  );
}
